import React from 'react'
import {
    observable, autorun, reaction, makeObservable,
    runInAction,
} from 'mobx'
import i18n from 'i18next'
import { v4 as uuidv4 } from 'uuid'
import * as R from 'ramda'

import { Survey } from '../../Models/Survey'
import { PageQuestion } from '../../Models/Page'
import { QuestionOption } from '../../Models/Question'

import {
    sortAsc, unescapeHtmlTags, getProductMap,
} from '../../utils/helpers'

import ProductSearch from './components/ProductSearch'
import ProductNew from './components/ProductNew'
import SurveyDetails from './components/SurveyDetails'
import ProductDetails from './components/ProductDetails'
import TemplateDetails from './components/TemplateDetails'
import SurveyCreate from './components/SurveyCreate'
import Distribute from './components/Distribute'
import {
    dropdownTopCountries,
    dropdownTopLangs,
    excludeTemplateIds,
    defaultProdDetails,
    surveyOptionEnum as importedSurveyOptionEnum,
} from './constants'
import Preview from '../Preview/Preview'
import { templateLicenceTypes } from '../../utils/Globals/SurveyFieldOptions'

class WizardStore {
    @observable survey = {}

    @observable surveyType

    @observable showLocaleWarning = false

    @observable curStage = 0

    @observable stages

    @observable productOptions = []

    @observable surveyAvailableTags = []

    @observable surveyRecordTags = []

    @observable productAvailableTags = []

    @observable productRecordTags = []

    @observable surveyDetails = {
        formData: {
            surveyName: '',
            locales: undefined,
            defaultLocale: undefined,
            respondentType: undefined,
            surveyType: undefined,
        },
        fetchData: {
            respondentTypes: [],
            surveyTypes: [],
            locales: [],
        },
        formFields: [],
        formLoading: true,
    }

    pageQuestions = new Map()

    @observable productDetails = {
        formData: {
            startDate: null,
            endDate: null,
            country: undefined,
            state: undefined,
            city: undefined,
            otherCity: undefined,
            venue: undefined,
            otherVenue: undefined,
            locales: undefined,
            defaultLocale: undefined,
            numVisitors: undefined,
            numExhibitors: undefined,
            eventContains: undefined,
            respondentType: undefined,
            eventType: undefined,
        },
        fetchData: {
            respondentTypes: [],
            eventType: [
                {
                    value: 'L',
                    label: 'A live / in-person event',
                },
                {
                    value: 'V',
                    label: 'A virtual / online event',
                },
                {
                    value: 'H',
                    label: 'A hybrid event (both live and virtual)',
                },
            ],
            countries: [],
            states: [],
            cities: [],
            venues: [],
            locales: [],
            numVisitors: [
                { value: 250, label: 'Less than 500' },
                { value: 500, label: '500 - 1,000' },
                { value: 1500, label: '1,000 - 2,000' },
                { value: 2500, label: '2,000 - 3,000' },
                { value: 4000, label: '3,000 - 5,000' },
                { value: 6250, label: '5,000 - 7,500' },
                { value: 8750, label: '7,500 - 10,000' },
                { value: 12500, label: '10,000 - 15,000' },
                { value: 20000, label: '15,000 - 25,000' },
                { value: 37500, label: '25,000 - 50,000' },
                { value: 75000, label: '50,000 - 100,000' },
                { value: 100000, label: 'More than 100,000' },
            ],
            numExhibitors: [
                { value: 5, label: 'Less than 10' },
                { value: 18, label: '10 - 25' },
                { value: 38, label: '26 - 50' },
                { value: 75, label: '51 - 100' },
                { value: 150, label: '101 - 200' },
                { value: 250, label: '201 - 300' },
                { value: 350, label: '301 - 400' },
                { value: 450, label: '401 - 500' },
                { value: 750, label: '501 - 1000' },
                { value: 1000, label: 'More than 1000' },
            ],
            eventContains: {
                S: i18n.t('views.survey.wizard.all.form.eventContains.session'),
                N: i18n.t('views.survey.wizard.all.form.eventContains.networking'),
                M: i18n.t('views.survey.wizard.all.form.eventContains.meeting'),
                B: i18n.t('views.survey.wizard.all.form.eventContains.booth'),
            },
        },
        formFields: [],
        formLoading: true,
    }

    @observable templateDetails = {
        formData: {
            surveyName: '',
            locales: undefined,
            defaultLocale: undefined,
            eventType: undefined,
            surveyType: undefined,
            templateLicenceType: undefined,
            respondentType: undefined,
            templateAccessBody: [],
            templateCustomTagBody: [],
        },
        fetchData: {
            respondentTypes: [],
            surveyTypes: [],
            eventTypes: [],
            locales: [],
        },
        formFields: [],
        formLoading: true,
        accessUpdateFn: undefined,
        customTagsUpdateFn: undefined,
        areCustomTagsParamsReady: false,
    }

    isTemplate = false

    isSurveyAiSummaryEnabled = true

    templateProducts = []

    prodMap = {}

    surveyOptionEnum = importedSurveyOptionEnum

    @observable disableFinishBtn = this.surveyType !== 'EM'

    @observable disableContinueBtn = false

    @observable surveyOptions = [
        {
            id: 0,
            name: i18n.t('surveyWizard.newCopy.clientTemp'),
            options: [],
            selected: null,
            color: 'purple',
            icon: 'fa-light fa-table-layout',
            onSelect: (obj, id) => this.setTemplate(obj, id),
            isHide: () => this.surveyType === 'EM',
            infoDescription: i18n.t('create.survey.wizard.tooltipText.clientTemplate', { clientName: this.clientName }),
        },
        {
            id: 1,
            name: i18n.t('views.survey.wizard.CE.stage.3.template.label'),
            options: [],
            selected: null,
            color: 'blue',
            icon: 'fa-light fa-layer-plus',
            onSelect: (obj, id) => this.setTemplate(obj, id),
            isHide: () => this.surveyType === 'EM',
            infoDescription: i18n.t('create.survey.wizard.tooltipText.exploriTemplate'),
        },
        {
            id: 2,
            name: i18n.t('views.survey.wizard.CE.stage.3.copy.label'),
            options: [],
            selected: null,
            icon: 'fa-light fa-clone',
            color: 'red',
            showBreaklink: Boolean(this.survey.breakLinks),
            onSelect: (obj, id) => this.setTemplate(obj, id),
            isHide: () => this.surveyType === 'EM' || this.isTemplate,
            infoDescription: i18n.t('create.survey.wizard.tooltipText.copySurvey'),
        },
        {
            id: 3,
            name: i18n.t('distribute.MAXBI.title'),
            color: 'green',
            icon: 'fa-light fa-check',
            onSelect: () => this.openSurvey(),
            btnLabel: i18n.t('distribute.MAXBI.completeLabel'),
            isHide: () => this.surveyType !== 'EM',
        },
        {
            id: 4,
            name: i18n.t('distribute.MAXBI.copySurvey'),
            color: 'red',
            icon: 'fa-light fa-clone',
            onSelect: () => this.copyToClipboard(),
            btnLabel: i18n.t('distribute.MAXBI.copySurvey.button'),
            surveyUrl: '',
            isHide: () => this.surveyType !== 'EM',
        },
        {
            id: 5,
            name: 'Create blank survey',
            selected: null,
            isHide: () => true,
        },
    ]

    @observable selectedProduct = null

    @observable handleValidate

    product = {}

    @observable templateLocales = []

    @observable clientLocales = []

    @observable surveyLoading = true

    @observable surveySearchLoading = true

    @observable surveyCreateLoading = true

    surveyTypeOptions = {
        EV: [
            'PE',
            'GE',
        ],
        PU: [
            'PE',
            'GE',
        ],
        VA: [
            'PE',
            'GE',
        ],
        CE: [
            'PE',
            // "GE",
        ],
        EXPO: [
            'PE',
            'GE',
        ],
        DENT: [
            'PE',
            'GE',
        ],
    }

    @observable hideProductDetails = false

    @observable hideProductNew = true

    respondentTypesOrderEV = ['Visitor', 'Exhibitor', 'PRNA', 'None']

    respondentTypesOrderCE = ['Visitor', 'Staff']

    clientName = ''

    isRapidSurvey = false

    constructor(store, industry, surveyType) {
        makeObservable(this)
        this.appStore = store
        this.industry = industry
        this.surveyType = surveyType
        this.clientName = store.userData.clientName

        this.stages = this.setWizardStages()

        this.survey = this.initSurvey(industry, surveyType)

        autorun(() => {
            if (this.surveyDetails.formFields.length > 0) this.surveyDetails.formLoading = false
            if (this.productDetails.formFields.length > 0) this.productDetails.formLoading = false
            if (this.templateDetails.formFields.length > 0) this.templateDetails.formLoading = false
        })

        reaction(
            () => [
                this.survey.metadata.taxonomy.respondentType,
                this.survey.metadata.taxonomy.surveyType,
                this.survey.metadata.productInfo.industrySpecificInfo.productType,
            ],
            async ([respondentType, surveyTypeId, productType]) => {
                this.checkSurvey(respondentType, surveyTypeId, productType)
                this.surveyAvailableTags = await this.getSurveyAvailableTags()
                this.createProductDetailsForm()
                this.createSurveyDetailsForm()
            },
        )

        reaction(
            () => [this.hideProductDetails, this.hideProductNew],
            () => {
                this.stages = this.setWizardStages()
            },
        )
        reaction(
            () => [this.templateDetails.formData.respondentType],
            ([respondentType]) => {
                if (respondentType) this.templateDetails.areCustomTagsParamsReady = true
            },
        )
    }

    initSurvey = (industry, surveyType) => {
        console.log('initSurvey', industry, surveyType)
        const survey = new Survey({
            content: {},
            pages: [],
            metadata: {
                clientId: this.appStore.tokenData.clientId,
                locales: [],
                productInfo: {
                    industrySpecificInfo: {
                        industry,
                    },
                },
                taxonomy: {
                    surveyType: undefined,
                    respondentType: undefined,
                    templateLicenceType: undefined,
                },
                creationInfo: {
                    createdBy: this.appStore.tokenData.userId,
                },
                adminConsole: {},
                displaySettings: {},
            },
            skipLogic: [],
        })

        if (industry === 'CE') {
            survey.metadata.taxonomy.surveyType = 'PE'
        }
        if (industry === 'DENT') {
            survey.metadata.taxonomy.respondentType = 'DEVI'
        }
        if (surveyType === 'EM') {
            survey.metadata.taxonomy.surveyType = surveyType
            survey.metadata.taxonomy.respondentType = 'CEVI'
            survey.metadata.productInfo.industrySpecificInfo.eventType = 'V'
            survey.metadata.productInfo.industrySpecificInfo.venue = undefined
            this.productDetails.formData.eventType = 'V'
        }
        if (surveyType === 'R') {
            // Rapid Survey
            this.isRapidSurvey = true
            survey.metadata.taxonomy.surveyType = 'PE'
            survey.metadata.productInfo.industrySpecificInfo.venueId = defaultProdDetails.venueId
        }
        if (surveyType === 'T') {
            this.isTemplate = true
            survey.metadata.productInfo.industrySpecificInfo.venueId = defaultProdDetails.venueId
        }
        return survey
    }

    initProductSearch = async (industry, surveyType) => {
        const { survey } = this
        const { metadata: { productInfo } } = survey

        this.surveySearchLoading = true
        try {
            this.productOptions = await this.getProducts(industry, surveyType)

            this.selectedProduct = this.productOptions.find(
                ({ value }) => value.productId === productInfo.productId,
            ) || null
        } catch (error) {
            console.log('initProductSearch ', error)
        }
        this.surveySearchLoading = false
    }

    initProductDetails = async () => {
        try {
            const { appStore } = this
            const [countries, respondentTypes] = await Promise.all([
                this.getCountries(),
                this.getSurveyRespondentTypes(this.industry),
            ])

            this.productDetails.fetchData.countries = this.reorder(countries, dropdownTopCountries)
            this.productDetails.fetchData.respondentTypes = respondentTypes
            this.productDetails.fetchData.locales = this.reorder(this.appStore.localeOptions, dropdownTopLangs)
            if (this.appStore.localeOptions.length === 1) {
                // eslint-disable-next-line prefer-destructuring
                this.productDetails.formData.locales = appStore.localeOptions
                this.productDetails.formData.defaultLocale = appStore.localeOptions[0]
            }

            if (this.survey.metadata.taxonomy.respondentType === 'VI') this.getDelegates('numVisitors')
            else this.getDelegates('numExhibitors')

            if (this.industry === 'CE' && this.surveyType !== 'EM') {
                this.survey.metadata.taxonomy.respondentType = this.survey.metadata.productInfo.industrySpecificInfo.productType !== 'CE3P'
                    ? 'CEVI' : undefined
                this.productDetails.formData.respondentType = this.survey.metadata.taxonomy.respondentType
            }
            this.createProductDetailsForm()
        } catch (error) {
            console.log('initProductDetails ', error)
        }
    }

    initSurveyDetails = async () => {
        try {
            const { appStore } = this
            const { industry, productType } = this.survey.metadata.productInfo.industrySpecificInfo

            const respondentTypes = await this.getSurveyRespondentTypes(this.industry)
            this.surveyDetails.fetchData.respondentTypes = respondentTypes
            this.surveyDetails.fetchData.surveyTypes = this.getSurveyTypeOptions(industry, productType)

            this.surveyDetails.fetchData.locales = this.reorder(this.appStore.localeOptions, dropdownTopLangs)

            if (this.appStore.localeOptions.length === 1) {
                // eslint-disable-next-line prefer-destructuring
                this.surveyDetails.formData.locales = appStore.localeOptions
                this.surveyDetails.formData.defaultLocale = appStore.localeOptions[0]
            }
            this.createSurveyDetailsForm()
        } catch (error) {
            console.log('initSurveyDetails ', error)
        }
    }

    initTemplateDetails = async () => {
        try {
            const { appStore } = this
            const { industry, productType } = this.survey.metadata.productInfo.industrySpecificInfo
            const respondentTypes = await this.getSurveyRespondentTypes(this.industry)
            this.templateDetails.fetchData.respondentTypes = respondentTypes
            this.templateDetails.fetchData.surveyTypes = this.getSurveyTypeOptions(industry, productType)
            this.templateDetails.fetchData.templateLicenceTypes = templateLicenceTypes()
            this.templateDetails.fetchData.locales = this.reorder(this.appStore.localeOptions, dropdownTopLangs)

            if (this.appStore.localeOptions.length === 1) {
                // eslint-disable-next-line prefer-destructuring
                this.templateDetails.formData.locales = appStore.localeOptions
                this.templateDetails.formData.defaultLocale = appStore.localeOptions[0]
            }
            this.templateProducts = await this.getTemplateProducts()
            this.prodMap = await getProductMap(appStore.http, this.appStore.tokenData.clientId, true)
            this.setTemplateProdMapOptions()
            this.createTemplateDetailsForm()
        } catch (error) {
            console.log('initSurveyDetails ', error)
        }
    }

    setTemplateProdMapOptions = () => {
        const industryEvents = this.prodMap[this.industry].children
        const eventTypes = []

        Object.entries(industryEvents).forEach(([key, ieData]) => {
            const { productId, productName, portfolioId } = this.templateProducts.find((tp) => (
                !excludeTemplateIds.includes(tp.productId)
                && tp.productType === key
            ))
            if (productId) {
                eventTypes.push({
                    label: ieData.label,
                    type: key,
                    value: productId, // actual event/product id
                    productName,
                    portfolioId,
                })
            }
        })
        this.templateDetails.fetchData.eventTypes = eventTypes

        const selectedEventType = this.templateDetails.formData.eventType
        if (selectedEventType) {
            this.templateDetails.fetchData.surveyTypes = eventTypes[selectedEventType].children
        }
        const selectedSurveyType = this.templateDetails.formData.surveyType
        if (selectedEventType) {
            this.templateDetails.fetchData.respondentTypes = eventTypes[selectedSurveyType].children
        }
    }

    initSurveyCreate = async () => {
        const { CLIENT_TEMPLATE, CLIENT_SURVEYS, EXPLORI_TEMPLATE } = this.surveyOptionEnum

        this.surveyCreateLoading = true
        try {
            const [clientSurveys, exploriTemplates] = await Promise.all([
                this.getClientSurveys(),
                process.env.NEW_TEMPLATING ? this.getExploriTemplatesV2() : this.getExploriTemplates(),
            ])

            if (this.surveyType !== 'EM') {
                const clientTemplates = await (process.env.NEW_TEMPLATING
                    ? this.getClientTemplatesV2()
                    : this.getClientTemplates())
                this.surveyOptions[CLIENT_TEMPLATE].options = clientTemplates
                this.surveyOptions[CLIENT_TEMPLATE].isHide = () => clientTemplates.length === 0
            }
            this.surveyOptions[CLIENT_SURVEYS].options = clientSurveys
            this.surveyOptions[EXPLORI_TEMPLATE].options = exploriTemplates

            if (this.surveyType === 'EM') {
                const clientTemplates = await this.getMaxbiClientSurveys()
                await this.initMaxbiDistribute(clientTemplates, exploriTemplates)
            }
        } catch (error) {
            console.error('initSurveyCreate ', error)
        }
        this.surveyCreateLoading = false
    }

    reorder = (array, priorityValues) => {
        const { priority, rest } = array.reduce((acc, item) => {
            if (priorityValues.has(item.value)) acc.priority.push(item)
            else acc.rest.push(item)
            return acc
        }, { priority: [], rest: [] })
        // Sort priority items according to position in original set
        const priorityArray = [...priorityValues]
        priority.sort((a, b) => priorityArray.indexOf(a.value) - priorityArray.indexOf(b.value))
        return [...priority, ...rest]
    }

    checkSurvey = (respondentType, surveyType, productType) => {
        const { industry } = this
        if (['CE', 'EV'].includes(industry) || surveyType === 'EM') return

        if (productType === 'VI') {
            this.survey.metadata.productInfo.industrySpecificInfo.venueId = 40
            this.survey.metadata.productInfo.industrySpecificInfo.eventContains = []
        }
        if (respondentType !== 'VI') {
            this.survey.metadata.productInfo.industrySpecificInfo.numVisitors = undefined
            this.productDetails.formData.numVisitors = undefined
        }
        if (respondentType !== 'EX') {
            this.survey.metadata.productInfo.industrySpecificInfo.numExhibitors = undefined
            this.productDetails.formData.numExhibitors = undefined
        }
        if (surveyType === 'GE' && this.industry !== 'EXPO') {
            this.survey.metadata.productInfo.industrySpecificInfo.startDate = undefined
            this.survey.metadata.productInfo.industrySpecificInfo.endDate = undefined
            this.survey.metadata.productInfo.industrySpecificInfo.venueId = undefined
            this.productDetails.formData.startDate = undefined
            this.productDetails.formData.endDate = undefined
        }
        if (surveyType === 'EM') {
            this.survey.metadata.productInfo.industrySpecificInfo.productType = 'CE3P'
        }
    }

    getSurveyTypeOptions = (industryCode, productType, respondentType) => {
        const { prodductMap } = this.appStore.client
        if (!industryCode || !productType) return []
        return Object.values(prodductMap[industryCode].children[productType].children)
            .filter(({ children }) => R.isNil(respondentType) || respondentType in children)
            .map(({ value, label }) => ({
                value,
                label,
            }), {})
    }

    getTemplateProducts = async () => this.appStore.http.get('/template/product')

    getProducts = async (industry, surveyType) => {
        const products = await this.appStore.http.get(`/wizard/products?industryCode=${industry}`)

        const productOptions = products.filter(({ producttypecode, status }) => {
            if (status === 5 && process.env.NEW_TEMPLATING) return false
            if (!surveyType || surveyType === 'R') {
                return !(producttypecode === 'CE3P' && status === 5)
            }
            if (producttypecode !== 'CE3P') {
                return false
            }
            if (!this.appStore.isExploriAdmin() && status === 5) {
                return false
            }
            return true
        }).map((product) => ({
            label: unescapeHtmlTags(product.nameedition),
            value: {
                productId: product.productId,
                productType: product.producttypecode,
                isOnsite: Boolean(product.onsite),
                label: unescapeHtmlTags(product.nameedition),
                portfolioId: product.portfolioId,
                customerJourney: product.customerJourney,
                status: product.status,
                divisionId: product.divisionId,
            },
        }))
        return productOptions
    }

    getCountries = async () => {
        const result = await this.appStore.http.get('/refcode/country')
        const countries = result.response.map(({ description, name }) => ({
            value: name,
            label: description,
        }))

        return sortAsc(countries)
    }

    getCities = async (countryCode, stateId) => {
        if (this.industry !== 'CE') {
            const cityData = await this.appStore.http.get(`/cities?country=${countryCode}&addOther=true`)
            return cityData.map(({ city }) => ({
                label: city,
                value: city,
            }))
        }

        const queryParam = stateId ? `?stateId=${stateId}&addOther=1` : '?addOther=1'
        const data = await this.appStore.http.get(`/cities/${countryCode}${queryParam}`)
        return data
    }

    getStates = (countryCode) => this.appStore.http.get(`/states/${countryCode}`)

    getVenues = async (cityLabel, countryId) => {
        const url = `/venues?city=${cityLabel}&active=1&addOther=true&country=${countryId}`
        const response = await this.appStore.http.get(url)
        const venues = response.map(({ name, venueId }) => ({
            label: name,
            value: venueId,
        }))
        return venues
    }

    validRapidSurveyResponded = (respondentType) => (
        !this.isRapidSurvey || ['EX', 'VI', 'CEVI', 'CEST'].includes(respondentType)
        // if enabling rapid VENU surveys:
        // !this.isRapidSurvey || ['EX', 'VI', 'CEVI', 'CEST', 'GST', 'ORG', 'CONT'].includes(respondentType)
    )

    getSurveyRespondentTypes = async (industry) => {
        const surveyRespondentTypes = await this.appStore.http.get('/surveyrespondenttypes')
        const respondentTypes = R.sortWith([
            R.ascend(R.prop('priority')),
        ], surveyRespondentTypes.respondentTypes).reduce(
            (arr, respondentType) => {
                if (
                    respondentType.industryCode === industry
                    && this.validRapidSurveyResponded(respondentType.respondentType)
                ) {
                    arr.push({
                        value: respondentType.respondentType,
                        label: respondentType.respondentTypeName,
                    })
                }
                return arr
            }, [],
        )

        return respondentTypes
    }

    getIndustry = () => (
        this.survey.metadata.productInfo.industrySpecificInfo.industry
    )

    getProductType = () => (
        this.survey.metadata.productInfo.industrySpecificInfo.productType
    )

    getRespondentType = () => (
        this.survey.metadata.taxonomy.respondentType
    )

    getSurveyType = () => (
        this.survey.metadata.taxonomy.surveyType
    )

    getSurveyAvailableTags = () => {
        const { portfolioId, divisionId } = this.survey.metadata.productInfo.industrySpecificInfo
        const [industry, productType, surveyType, respondentType] = [
            this.getIndustry(),
            this.getProductType(),
            this.getSurveyType(),
            this.getRespondentType(),
        ]
        const anyEmpty = [industry, productType, surveyType, respondentType].some(
            (field) => R.isNil(field) || R.isEmpty(field),
        )
        if (anyEmpty) return []

        return this.appStore.getAvailableTags({
            oid: 7,
            industry,
            divisionId,
            portfolioId,
            productType,
            surveyType,
            respondentType,
        })
    }

    getMaxbiClientSurveys = async () => {
        const { appStore } = this
        let clientTemplates = []
        try {
            clientTemplates = await appStore.http.get('/wizard/surveys?template=1&surveyType=EM&industryCode=CE&productType=CE3P')
        } catch (e) {
            console.log(e)
        }

        clientTemplates.map(({ label, value, publicId }) => (
            {
                label,
                value,
                publicId,
            }
        ))
        return clientTemplates
    }

    getClientSurveys = async () => {
        const { productId, industrySpecificInfo } = this.survey.metadata.productInfo
        const { productType, productName, portfolioId } = industrySpecificInfo
        const [industry, surveyType, respondentType] = [
            this.getIndustry(),
            this.getSurveyType(),
            this.getRespondentType(),
        ]
        let clientTemplates = []

        if (this.isRapidSurvey) {
            return clientTemplates
        }

        let url = '/wizard/surveys'
        url += `?industryCode=${industry}`
        url += `&portfolioId=${portfolioId}`
        url += `&surveyType=${surveyType}`
        url += `&respondentType=${respondentType}`
        url += `&productType=${productType}`

        const clientSurveys = await this.appStore.http.get(url)

        const productSurveys = []
        const eventSurveys = []
        const otherSurveys = []

        this.productSurveys = []

        clientSurveys.forEach((s) => {
            if (s.productId === productId) {
                this.productSurveys.push(s.value)
                productSurveys.push({
                    label: s.label,
                    value: s.value,
                    publicId: s.publicId,
                })
            } else {
                eventSurveys.push({
                    label: s.label,
                    value: s.value,
                    publicId: s.publicId,
                })
            }
        })

        clientTemplates = [
            {
                label: `${productName} - surveys`,
                options: productSurveys,
            },
            {
                label: 'Surveys from other events',
                options: eventSurveys,
            },
            {
                label: 'Other',
                options: otherSurveys,
            },
        ]

        return clientTemplates
    }

    setSurveyTag = ({ name, value }) => {
        this.surveyRecordTags[name] = value
    }

    saveSurveyTags = async (recordId) => {
        const { appStore } = this
        const { surveyAvailableTags, surveyRecordTags } = this
        try {
            appStore.saveTags(recordId, 7, surveyAvailableTags, surveyRecordTags)
        } catch (e) {
            console.error(e)
        }
    }

    setProductTag = ({ name, value }) => {
        console.log('setTag', name, value)
        this.productRecordTags[name] = value
    }

    saveProductTags = async (recordId) => {
        const { appStore } = this
        const { productAvailableTags, productRecordTags } = this
        try {
            appStore.saveTags(recordId, 6, productAvailableTags, productRecordTags)
        } catch (e) {
            console.error(e)
        }
    }

    setTemplateTags = async () => {
        const body = await this.templateDetails.customTagsUpdateFn?.() || []
        this.templateDetails.formData.templateCustomTagBody = body
    }

    getTagTypes = () => this.surveyAvailableTags.reduce((types, { tagId, questionType }) => (
        Object.assign(types, {
            [tagId]: questionType,
        })
    ), {})

    cleanSurveyTags = (surveyRecordTags) => {
        const tagTypes = this.getTagTypes()
        return Object.keys(surveyRecordTags)
            .filter((k, i) => surveyRecordTags[k] !== undefined && ['SB', 'CH'].includes(tagTypes[i]))
            .flatMap((k) => [].concat(surveyRecordTags[k]).map((v) => [Number(k), v]))
    }

    getExploriTemplates = async () => {
        const { metadata: { taxonomy } } = this.survey
        let templates = []

        let url = '/wizard/surveys'
        url += '?explori=1'
        url += `&surveyType=${taxonomy.surveyType}`
        url += `&respondentType=${this.getRespondentType()}`
        url += `&productType=${this.getProductType()}`
        if (this.isRapidSurvey) {
            url += '&isRapidSurvey=1'
        }

        try {
            templates = await this.appStore.http.get(url)
        } catch (e) {
            console.log(e)
        }

        return templates.map(({ label, value, publicId }) => ({
            label,
            value,
            publicId,
        }))
    }

    getExploriTemplatesV2 = async () => {
        const { metadata: { productInfo } } = this.survey
        let templates = []
        const surveyTags = this.cleanSurveyTags(this.surveyRecordTags)

        let url = '/template'
        url += `/${productInfo.productId}`
        url += `/${this.getProductType()}`
        url += `/${this.getSurveyType()}`
        url += `/${this.getRespondentType()}`
        url += '?explori=true'
        if (this.isRapidSurvey) {
            url += '&isRapidSurvey=1'
        }
        if (surveyTags.length > 0) {
            url += `&surveyTags=${encodeURIComponent(JSON.stringify(surveyTags))}`
        }

        try {
            templates = await this.appStore.http.get(url)
        } catch (e) {
            console.log(e)
        }

        return templates.map(({ id, name, publicId }) => ({
            value: id,
            label: name.replace(/&amp;/gi, '&'),
            publicId,
        }))
    }

    getClientTemplates = async () => {
        const { metadata: { taxonomy, productInfo } } = this.survey
        const { industrySpecificInfo } = productInfo
        let clientTemplates = []

        let url = '/wizard/surveys'
        url += `?industryCode=${industrySpecificInfo.industry}`
        url += '&template=1'
        url += `&surveyType=${taxonomy.surveyType}`
        url += `&respondentType=${taxonomy.respondentType}`
        url += `&productType=${industrySpecificInfo.productType}`
        if (this.isRapidSurvey) {
            url += '&isRapidSurvey=1'
        }

        try {
            clientTemplates = await this.appStore.http.get(url)
        } catch (e) {
            console.log(e)
        }

        return clientTemplates.map(({ value, label, publicId }) => ({
            value,
            label: label.replace(/&amp;/gi, '&'),
            publicId,
        }))
    }

    getClientTemplatesV2 = async () => {
        const { metadata: { productInfo } } = this.survey
        let clientTemplates = []
        const surveyTags = this.cleanSurveyTags(this.surveyRecordTags)

        let url = '/template'
        url += `/${productInfo.productId}`
        url += `/${this.getProductType()}`
        url += `/${this.getSurveyType()}`
        url += `/${this.getRespondentType()}`
        if (this.isRapidSurvey) {
            url += '?isRapidSurvey=1'
        }
        if (surveyTags.length > 0) {
            const firstParam = !this.isRapidSurvey
            const prefix = firstParam ? '?' : '&'
            url += `${prefix}surveyTags=${encodeURIComponent(JSON.stringify(surveyTags))}`
        }

        try {
            clientTemplates = await this.appStore.http.get(url)
        } catch (e) {
            console.log(e)
        }

        return clientTemplates.map(({ id, name, publicId }) => ({
            value: id,
            label: name.replace(/&amp;/gi, '&'),
            publicId,
        }))
    }

    getQuestionLevel = (oid) => {
        let level = 'Product'
        if (oid === 1) {
            level = 'Explori'
        } else if (oid === 3) {
            level = 'Client'
        } else if (oid === 4) {
            level = 'Division'
        } else if (oid === 5) {
            level = 'Portfolio'
        }

        return level
    }

    loadCopySurvey = async (surveyId) => {
        const { appStore, survey } = this
        const { metadata: { productInfo } } = survey

        try {
            this.pageQuestions = new Map()
            const { portfolioId } = await appStore.http.get(`/portfolioid?productId=${productInfo.productId}`)

            const questionUrl = `/api/survey/${surveyId}/questions?`
                + `portfolioId=${portfolioId}`
                + `&surveyType=${survey.metadata.taxonomy.surveyType}`
                + `&respondentType=${this.getRespondentType()}&productType=${this.getProductType()}&industry=${this.getIndustry()}`

            const [surveyQuestions, copySurvey] = await Promise.all([
                appStore.http.get(questionUrl),
                appStore.http.get(`/api/survey/${surveyId}`, appStore.surveyToken),
            ])

            const surveyLocale = copySurvey.metadata.defaultLocale
            const smartGrids = surveyQuestions
                .filter(({ question }) => ['BEH', 'COG'].includes(question.metadata.kpiType))
                .reduce((obj, { question }) => (
                    R.mergeLeft(obj, {
                        [question.id.toUpperCase()]: this.getQuestionLevel(question.metadata.oid),
                    })
                ), {})

            const sgQuestions = copySurvey.pages.reduce((q, page) => {
                const pq = R.pathOr([], ['questions'], page)
                    .filter(({ questionId }) => R.hasPath([questionId.toUpperCase()], smartGrids))
                    .map((qs) => ({
                        questionId: qs.questionId.toUpperCase(),
                        questionLevel: R.path([qs.questionId.toUpperCase()], smartGrids),
                        options: qs.optionSetY.options.map((qo) => {
                            const l = qo.langs[surveyLocale]
                                ? surveyLocale
                                : Object.keys(qo.langs)[0]
                            return qo.langs[l].text
                        }),
                    }))
                q.push(...pq)
                return q
            }, [])

            sgQuestions.forEach(({ questionId, options, questionLevel }) => {
                this.pageQuestions.set(questionId, {
                    questionLevel,
                    options: options.map((o) => ({
                        value: o,
                        key: uuidv4(),
                    })),
                })
            })
        } catch (e) {
            console.log(`Error loading copy survey ${e}`)
        }
    }

    resetSelectedOptions = () => {
        const {
            CLIENT_TEMPLATE, CLIENT_SURVEYS, EXPLORI_TEMPLATE, BLANK_SURVEY,
        } = this.surveyOptionEnum
        this.surveyOptions[CLIENT_TEMPLATE].selected = null
        this.surveyOptions[CLIENT_SURVEYS].selected = null
        this.surveyOptions[CLIENT_SURVEYS].showBreaklink = false
        this.surveyOptions[EXPLORI_TEMPLATE].selected = null
        this.surveyOptions[BLANK_SURVEY].selected = false
    }

    setDefaultLocale = (obj) => {
        this.surveyDetails.formData.defaultLocale = obj.value
        runInAction(() => {
            this.disableFinishBtn = false
            this.disableContinueBtn = false
        })
        console.log('HELLO', this.disableContinueBtn, this.disableFinishBtn)
    }

    getSurvey = async (id) => {
        this.surveyLoading = true
        const surv = await this.appStore.http.get(`/api/survey/${id}`)
        this.templateLocales = surv.metadata.locales
        const clientLangs = this.appStore.clientLanguages.map((lan) => lan.langcode)
        this.clientLocales = this.templateLocales.filter((loc) => clientLangs.includes(loc))
        this.surveyLoading = false
    }

    setTemplate = async (obj, id) => {
        if (this.surveyOptions[id].selected === obj) return
        this.resetSelectedOptions()
        this.surveyOptions[id].selected = obj
        this.survey.breakLinks = false
        this.survey.copyOf = obj.value
        this.surveyDetails.formData.defaultLocale = undefined
        runInAction(() => {
            this.disableFinishBtn = true
            this.disableContinueBtn = true
        })

        if (this.surveyOptionEnum.CLIENT_SURVEYS === id) {
            this.loadCopySurvey(obj.value)
            this.surveyOptions[id].showBreaklink = this.productSurveys.includes(obj.value)
            if (!this.surveyOptions[id].showBreaklink) {
                this.survey.breakLinks = false
            }
        }
        if (obj.value) {
            await this.getSurvey(obj.value)
        }
    }

    openSurvey = () => {
        const { MAXBI_COPY } = this.surveyOptionEnum
        const url = this.surveyOptions[MAXBI_COPY].surveyUrl
        window.open(url, '_blank')
    }

    copyToClipboard = () => {
        const { MAXBI_COPY } = this.surveyOptionEnum
        const text = this.surveyOptions[MAXBI_COPY].surveyUrl
        navigator.clipboard.writeText(text).then(() => {
            this.appStore.addNotification(null, 'Copied to clipboard', 'success')
        })
    }

    delay = (time) => new Promise((resolve) => setTimeout(resolve, time))

    getSurveyToken = async (limit, surveyID) => {
        let token = null
        try {
            const tokenFetchRes = await this.appStore.http.get(`/${surveyID}/surveyToken`, undefined, 4000)
            token = tokenFetchRes.token
            if (!token) throw new Error('empty token')
            return token
        } catch (e) {
            if (!token && limit > 0) {
                await this.delay(1000)
                return this.getSurveyToken(--limit, surveyID)
            }
            throw new Error('Unable to get survey token')
        }
    }

    createMaxbiUser = async (surveyID) => {
        const body = {
            email: '',
            surveyuserlistid: '00000000-0000-0000-0000-000000000000',
            type: 1,
            surveyUserId: 0,
        }
        try {
            const surveyToken = await this.getSurveyToken(10, surveyID)
            const surveyUser = await this.appStore.http.post(`/surveyuser/${surveyID}`, JSON.stringify(body), surveyToken)
            this.appStore.addNotification(null, 'Survey user created', 'success')
            return surveyUser.surveyUserId
        } catch (e) {
            this.appStore.addNotification(null, 'Unable to create survey user', 'error')
        }
        return null
    }

    setCopyOf = async (obj) => {
        if (!R.hasPath(['value'], obj)) return
        this.survey.copyOf = obj.value
        await this.loadCopySurvey(this.survey.copyOf)
    }

    initMaxbiDistribute = async (clientSurveys, exploriTemplates) => {
        const { MAXBI_COPY } = this.surveyOptionEnum
        await this.setCopyOf(clientSurveys.length > 0 ? clientSurveys[0] : exploriTemplates[0])
        const maxbiSurveyId = await this.createSurvey(true)
        if (!maxbiSurveyId) return
        const maxbiSurveyUserId = await this.createMaxbiUser(maxbiSurveyId)
        const { publicId } = await this.appStore.http.get(`/api/survey/${maxbiSurveyId}`, this.surveyToken)
        this.survey.publicId = publicId
        this.surveyOptions[MAXBI_COPY].surveyUrl = maxbiSurveyUserId
            ? this.survey.getUserLink(maxbiSurveyUserId, true) : ''
        this.disableFinishBtn = false
    }

    isAdminConsoleEnabled = () => true // legacy code -> R.path(['userData', 'flags', 'isClientOnsite'], this.appStore) || this.productIsOnsite

    surveyToJSON = () => {
        const { survey } = this
        const {
            metadata,
            metadata: { taxonomy, productInfo: { industrySpecificInfo, productId } },
        } = survey
        const questions = (this.industry === 'CE' && survey.metadata.taxonomy.surveyType !== 'EM')
            ? Array.from(this.pageQuestions).map(([key, data], index) => (
                new PageQuestion({
                    questionId: key,
                    priority: (index + 1),
                    questionLevel: data.questionLevel,
                    optionSetY: {
                        options: data.options
                            .filter((option) => option.value !== '')
                            .map((option, priority) => (
                                new QuestionOption({
                                    langs: {
                                        en_GB: {
                                            text: option.value,
                                        },
                                    },
                                    priority: (priority + 1),
                                })
                            )),
                    },
                }).toJSON()
            )) : undefined

        const { productName, endDate } = industrySpecificInfo
        const { city } = this.productDetails.formData

        let { name } = survey

        if (this.industry === 'CE' && !this.isTemplate && !this.isRapidSurvey) {
            name = taxonomy.surveyType === 'EM'
                ? `Maxbi - ${productName} ${new Date(endDate).getFullYear()}`
                : `${productName} ${(city ? city.label : '')} ${new Date(endDate).getFullYear()}`
        }

        if (this.isRapidSurvey) {
            survey.metadata.productInfo.industrySpecificInfo.numVisitors = defaultProdDetails.numVisitors
            survey.metadata.productInfo.industrySpecificInfo.numExhibitors = defaultProdDetails.numExhibitors
        }

        if (['R', 'PC', 'W', 'P365', 'WV', 'VE', 'SP'].includes(taxonomy.surveyType)) {
            industrySpecificInfo.venueId = defaultProdDetails.venueId
        }

        if (industrySpecificInfo.industry === 'CE' && industrySpecificInfo.eventType === 'V') {
            // set venue to be empty for virtual events
            industrySpecificInfo.venue = ''
        }

        const json = {
            name,
            createdBy: metadata.creationInfo.createdBy,
            productId,
            clientId: metadata.clientId,
            locales: metadata.locales,
            defaultLocale: metadata.defaultLocale,
            taxonomy: taxonomy.toJSON(),
            industrySpecificInfo: industrySpecificInfo.toJSON(),
            adminConsoleEnabled: this.isAdminConsoleEnabled(),
            copyOf: survey.copyOf,
            pageQuestions: questions,
            breakLinks: survey.breakLinks,
            mergefields: this.mergefields,
            isPrimary: this.isPrimarySurvey,
            isRapidSurvey: this.isRapidSurvey,
            isSurveyAiSummaryEnabled: this.isSurveyAiSummaryEnabled,
        }

        if (this.isTemplate) json.closeDate = metadata.closeDate

        return json
    }

    checkVenue = () => {
        const { survey, appStore } = this
        const { venue, venueId } = survey.metadata.productInfo.industrySpecificInfo
        const city = venue ? venue.city?.value || venue.otherCity : ''
        return new Promise((resolve, reject) => {
            if (![undefined, -1].includes(venueId) || this.hideProductDetailsFields(this.industry, 'otherVenue')) {
                resolve()
            } else {
                const venueData = {
                    venueId: 0,
                    name: venue.otherVenue,
                    city,
                    country: venue.country.value,
                    active: 0,
                }
                appStore.http.post('/venue', JSON.stringify(venueData))
                    .then((response) => {
                        survey.metadata.productInfo.industrySpecificInfo.venueId = response.venueId
                        if (survey.metadata.productInfo.industrySpecificInfo.industry === 'CE') {
                            survey.metadata.productInfo.industrySpecificInfo.venue = {
                                country: venue.country,
                                city: !venue.otherCity ? venue.city : {
                                    value: venue.otherCity,
                                    label: venue.otherCity,
                                },
                                state: venue.state,
                                venue: {
                                    value: response.venueId,
                                    label: venue.otherVenue,
                                },
                            }
                        }
                        resolve()
                    })
                    .catch((e) => {
                        console.log(e)
                        reject(e)
                    })
            }
        })
    }

    checkPrimary = async () => {
        const { survey } = this
        const { metadata: { taxonomy, productInfo, closeDate } } = survey
        if (taxonomy.surveyType !== 'PE') {
            this.isPrimarySurvey = undefined
            return
        }

        let url = '/survey/isPrimary?'
        url += `surveyType=${taxonomy.surveyType}`
        url += `&respondentType=${this.getRespondentType()}`
        url += `&year=${closeDate.year()}`
        url += `&productId=${productInfo.productId}`
        const primaryCheck = await this.appStore.http.get(url)
        this.isPrimarySurvey = !(
            Array.isArray(primaryCheck.primarySurvey)
            && primaryCheck.primarySurvey.length
        )
    }

    isSurveyCreated = async (surveyId, startTime) => {
        const { appStore } = this
        const { survey } = appStore
        const that = this
        const currTime = new Date().getTime()
        if ((currTime - startTime) > 20000) {
            appStore.surveyCloneConfirm = false
            appStore.addNotification(null, 'survey was not created within 20s', 'error')
        } else {
            try {
                const res = await appStore.http.get(`/survey/${surveyId}`)
                if (res.name === 'NotFound') {
                    survey.timeout -= 1
                    setTimeout(() => that.isSurveyCreated(surveyId, startTime), 1000)
                } else if (this.surveyType === 'EM') {
                    window.location.href = '/maxbi'
                } else if (!this.isRapidSurvey) {
                    // store.surveyCloneConfirm = false
                    window.location.href = `/survey/${surveyId}/setup`
                }
            } catch (e) {
                if (e.status === 404) {
                    // survey not found
                    survey.timeout -= 1
                    setTimeout(() => that.isSurveyCreated(surveyId, startTime), 1000)
                } else {
                    appStore.surveyCloneConfirm = false
                    const msg = `Error ${survey.copying ? 'cloning' : 'creating'} survey`
                    appStore.addNotification(null, msg, 'error')
                }
                console.log(e)
            }
        }
    }

    createRapidSurvey = async () => {
        try {
            this.disableContinueBtn = true
            this.surveyId = await this.createSurvey(true)
            this.appStore.surveyToken = await this.getSurveyToken(10, this.surveyId)

            await this.appStore.http.post(`/survey/${this.surveyId}/campaign`, JSON.stringify({
                campaignType: 'campaign',
                domain: null,
                langcode: this.surveyDetails.formData.defaultLocale,
                onSite: false,
                title: 'Campaign',
                type: 1,
            }), this.appStore.surveyToken)
            const survey = await this.appStore.http.get(`/api/survey/${this.surveyId}`, this.appStore.surveyToken)
            this.survey.publicId = survey.publicId
            this.appStore.survey = new Survey(survey)
            const [campaign] = await this.appStore.http.get(`/survey/${this.surveyId}/campaigns?onsite=0&type=campaign`, this.appStore.surveyToken)
            this.campaignLink = this.survey.getCampaignLink(campaign.uuid, campaign.langCode, campaign.useLatestUI)
            this.goNext()
            this.disableContinueBtn = false
        } catch (e) {
            console.error(e)
            this.appStore.addNotification(null, 'Error creating survey', 'error')
            this.disableContinueBtn = false
        }
    }

    createTemplate = async (noredirect = false) => {
        try {
            this.survey.metadata.defaultLocale = this.surveyDetails.formData.defaultLocale
            this.survey.metadata.locales = [this.surveyDetails.formData.defaultLocale]
            const response = await this.appStore.http.post('/api/survey/new', JSON.stringify(this.surveyToJSON()))
            if (!response.newId) {
                this.appStore.addNotification(null, `${response.name}, ${response.detail}`, 'error')
                return undefined
            }
            const templateId = response.newId
            await this.handleUpdateTemplateAccess(templateId)
            await this.handleUpdateTemplateCustomTags(templateId)

            this.appStore.addNotification(null, 'Creating survey', 'success')
            this.appStore.surveyCloneConfirm = true
            if (!noredirect) {
                await this.isSurveyCreated(templateId, new Date().getTime())
            }
            return templateId
        } catch (e) {
            this.appStore.addNotification(null, R.pathOr('Server error', ['message', 'name'], e), 'error')
            return undefined
        }
    }

    createSurvey = async (noredirect = false) => {
        try {
            await this.checkVenue()
            await this.checkPrimary()
            if (this.surveyType === 'EM') {
                this.survey.metadata.defaultLocale = this.productDetails.formData.defaultLocale.value
                this.survey.metadata.locales = this.productDetails.formData.locales.map((loc) => loc.value)
            } else {
                this.survey.metadata.defaultLocale = this.surveyDetails.formData.defaultLocale
                this.survey.metadata.locales = [this.surveyDetails.formData.defaultLocale]
            }
            const response = await this.appStore.http.post('/api/survey/new', JSON.stringify(this.surveyToJSON()))
            if (!response.newId) {
                this.appStore.addNotification(null, `${response.name}, ${response.detail}`, 'error')
                return undefined
            }
            const surveyId = response.newId
            await this.saveSurveyTags(surveyId)

            this.appStore.addNotification(null, 'Creating survey', 'success')
            this.appStore.surveyCloneConfirm = true
            if (!noredirect) {
                await this.isSurveyCreated(surveyId, new Date().getTime())
            }
            return response.newId
        } catch (e) {
            this.appStore.addNotification(null, R.pathOr('Server error', ['message', 'name'], e), 'error')
            console.error(e)
            return undefined
        }
    }

    resetOptions = () => {
        this.pageQuestions?.forEach((value, key) => {
            this.pageQuestions?.set(key, {
                questionLevel: value.questionLevel,
                options: [],
            })
        })
    }

    setProductDetailsField = async ({ name, value, label }, changeMeta) => {
        const action = R.pathOr(undefined, ['action'], changeMeta)
        const option = R.pathOr(undefined, ['option'], changeMeta)
        if (name === 'respondentType') {
            this.survey.metadata.taxonomy.respondentType = value
        }
        if (name === 'eventType') {
            this.survey.metadata.productInfo.industrySpecificInfo.eventType = value
            this.survey.metadata.productInfo.industrySpecificInfo.venue = (value === 'V' ? undefined : {})
        }
        if (name === 'country') {
            this.productDetails.formData = {
                ...this.productDetails.formData,
                state: undefined,
                city: undefined,
                otherCity: undefined,
                venue: undefined,
            }
            if (value === 'US') {
                this.productDetails.fetchData.states = await this.getStates(value)
                this.productDetails.fetchData.cities = []
            } else {
                this.productDetails.fetchData.states = []
                this.productDetails.fetchData.cities = await this.getCities(value)
            }
            this.productDetails.formData.city = {
                value: undefined,
                label: 'Select here',
            }
            this.productDetails.fetchData.venues = []
        }
        if (name === 'city') {
            this.productDetails.formData = {
                ...this.productDetails.formData,
                otherCity: undefined,
                venue: undefined,
            }
            if (value !== -1) {
                this.productDetails.fetchData.venues = await this.getVenues(
                    label,
                    this.productDetails.formData.country.value,
                )
                this.productDetails.formData.venue = {
                    value: undefined,
                    label: 'Select here',
                }
            } else {
                this.productDetails.fetchData.venues = []
            }
        }
        if (name === 'state') {
            this.productDetails.formData = {
                ...this.productDetails.formData,
                city: undefined,
                otherCity: undefined,
                venue: undefined,
            }
            this.productDetails.fetchData.cities = await this.getCities(
                this.productDetails.formData.country.value, value,
            )
            this.productDetails.formData.city = {
                value: undefined,
                label: 'Select here',
            }
            this.productDetails.fetchData.venues = []
        }
        if (name === 'locales') {
            this.productDetails.formData.locales = value
            const hasDefaultLocale = this.productDetails.formData.locales.find(
                (locale) => (locale.value === this.productDetails.formData.defaultLocale?.value),
            )
            if (!hasDefaultLocale) {
                this.productDetails.formData.defaultLocale = this.productDetails.formData.locales[0]
            }

            if (action === 'select-option') {
                const enLocales = ['en_GB', 'en_US']
                const selectedOption = R.pathOr('', ['value'], option)
                const localeSet = value.reduce((set, obj) => set.add(obj.value), new Set())

                this.showLocaleWarning = (
                    enLocales.includes(selectedOption)
                    && enLocales.every((l) => localeSet.has(l))
                )
            }
        } else if (name === 'otherCity') {
            this.productDetails.formData[name] = value
            this.productDetails.fetchData.venues = await this.getVenues(
                value,
                this.productDetails.formData.country.value,
            )
        } else if (['otherCity', 'otherVenue', 'endDate', 'startDate', 'eventContains'].includes(name)) {
            this.productDetails.formData[name] = value
        } else {
            this.productDetails.formData[name] = { label, value }
        }

        this.createProductDetailsForm()
    }

    setTemplateDetailsField = (obj) => {
        const { name, value, label } = obj
        switch (name) {
            case 'surveyName':
                this.templateDetails.formData[name] = value
                break
            case 'eventType': {
                this.templateDetails.formData[name] = (value === undefined) ? undefined : { label, value }
                this.survey.metadata.productInfo.productId = value
                this.survey.metadata.productInfo.industrySpecificInfo.productName = obj.productName

                const surveyTypesOpts = this.templateDetails.fetchData.surveyTypes
                if (surveyTypesOpts.length === 1) {
                    const surveyType = surveyTypesOpts[0]
                    this.templateDetails.formData.surveyType = surveyType
                    this.survey.metadata.taxonomy.surveyType = surveyType.value
                }
                break
            }
            case 'surveyType': {
                this.survey.metadata.taxonomy.surveyType = value
                this.templateDetails.formData[name] = (value === undefined) ? undefined : { label, value }

                const respTypeOpts = this.templateDetails.fetchData.respondentTypes
                if (respTypeOpts.length === 1) {
                    const respType = respTypeOpts[0]
                    this.templateDetails.formData.respondentType = respType
                    this.survey.metadata.taxonomy.respondentType = respType.value
                }
                break
            }
            case 'templateLicenceType': {
                this.survey.metadata.taxonomy.templateLicenceType = value
                this.templateDetails.formData[name] = value
                break
            }
            case 'respondentType': {
                this.survey.metadata.taxonomy.respondentType = value
                this.templateDetails.formData[name] = value
                if (['NN', 'SE'].includes(value)) {
                    this.setTemplateDetailsField({ name: 'surveyType', value: 'GE', label: '' })
                } else if (value === 'EX') {
                    const { numExhibitors } = defaultProdDetails
                    this.survey.metadata.productInfo.industrySpecificInfo.numExhibitors = numExhibitors
                }
                break
            }
            case 'locales': {
                this.templateDetails.formData.locales = value
                const hasDefaultLocale = this.templateDetails.formData.locales.find(
                    (locale) => locale.value === this.templateDetails.formData.defaultLocale?.value,
                )
                if (!hasDefaultLocale) {
                    this.templateDetails.formData.defaultLocale = this.templateDetails.formData.locales[0]
                }
                break
            }
            default:
                this.templateDetails.formData[name] = { label, value }
                break
        }

        this.createTemplateDetailsForm()
    }

    setSurveyDetailsField = async (obj, changeMeta) => {
        const { name, value, label } = obj
        const action = R.pathOr(undefined, ['action'], changeMeta)
        const option = R.pathOr(undefined, ['option'], changeMeta)

        if (name === 'respondentType') {
            this.survey.metadata.taxonomy.respondentType = value

            const { productType } = this.survey.metadata.productInfo.industrySpecificInfo

            if (['NN', 'SE'].includes(value)) {
                this.setSurveyDetailsField({ name: 'surveyType', value: 'GE', label: '' })
            } else if (this.industry === 'EV') {
                const { industrySpecificInfo } = this.survey.metadata.productInfo
                this.surveyDetails.fetchData.surveyTypes = this.getSurveyTypeOptions(this.industry, productType, value)
                if (!this.surveyDetails.fetchData.surveyTypes.map(
                    (a) => a.value,
                ).includes(this.survey.metadata.taxonomy.surveyType)
                ) {
                    this.setSurveyDetailsField({ name: 'surveyType', value: undefined, label: '' })
                }

                // reset numVisitors & numExhibitors after respondent type change in EV surveys
                this.productDetails.formData.numVisitors = undefined
                this.productDetails.formData.numExhibitors = undefined
                industrySpecificInfo.numVisitors = undefined
                industrySpecificInfo.numExhibitors = undefined
                this.createProductDetailsForm()
            }
        }
        if (name === 'surveyType') {
            this.survey.metadata.taxonomy.surveyType = value
            // remove ProductDetails stage if survey type is not PE
            if (['PE', 'R', 'PC', 'W', 'P365', 'WV', 'VE', 'SP'].includes(value)) {
                this.hideProductDetails = false
            } else {
                this.hideProductDetails = true
            }
        }

        // assign value to surveyDetails
        if (name === 'surveyName') {
            this.surveyDetails.formData[name] = value
        } else if (['startDate', 'endDate'].includes(name)) {
            this.surveyDetails.formData[name] = value
        } else if (name === 'surveyType') {
            this.surveyDetails.formData[name] = (value === undefined) ? undefined : { label, value }
        } else if (name === 'locales') {
            this.surveyDetails.formData.locales = value
            const hasDefaultLocale = this.surveyDetails.formData.locales.find(
                (locale) => (locale.value === this.surveyDetails.formData.defaultLocale?.value),
            )
            if (!hasDefaultLocale) {
                this.surveyDetails.formData.defaultLocale = this.surveyDetails.formData.locales[0]
            }
            if (action === 'select-option') {
                const enLocales = ['en_GB', 'en_US']
                const selectedOption = R.pathOr('', ['value'], option)
                const localeSet = value.reduce((set, { value }) => set.add(value), new Set())

                this.showLocaleWarning = (
                    enLocales.includes(selectedOption)
                    && enLocales.every((l) => localeSet.has(l))
                )
            }
        } else {
            this.surveyDetails.formData[name] = { label, value }
        }

        this.createSurveyDetailsForm()
    }

    createLocale = (hideField, formDetails) => ([
        {
            type: 'select',
            name: 'locales',
            label: i18n.t('surveyWizard.productDetails.locales'),
            placeholder: i18n.t('form.select.placeholder'),
            defaultValue: R.pathOr([], ['formData', 'locales'], formDetails).map((locale) => (locale.value)),
            options: formDetails.fetchData.locales,
            isMandatory: true,
            isSearchable: true,
            isMulti: true,
            errorMessage: `${i18n.t('surveyWizard.productDetails.locales')} ${i18n.t('required')}`,
            isHide: hideField(this.industry, 'locales') || formDetails.fetchData.locales.length < 2,
        },
        {
            type: 'select',
            name: 'defaultLocale',
            label: i18n.t('surveyWizard.productDetails.defaultLocale'),
            placeholder: i18n.t('form.select.placeholder'),
            value: formDetails.formData.defaultLocale,
            options: formDetails.formData.locales,
            isMandatory: true,
            errorMessage: `${i18n.t('surveyWizard.productDetails.defaultLocale')} ${i18n.t('required')}`,
            isHide: hideField(this.industry, 'defaultLocale') || R.pathOr([], ['formData', 'locales'], formDetails).length < 2,
        },
    ])

    getNumVisitorsLabel = (respondentType) => {
        let translation = ''
        switch (respondentType) {
            case 'VI':
                translation = 'views.survey.wizard.CE.stage.2.details.visitors.label'
                break
            case 'EX':
                translation = 'views.survey.wizard.CE.stage.2.details.exhibitors.label'
                break
            default:
                translation = 'views.survey.wizard.CE.stage.2.details.delegates.label'
                break
        }
        return i18n.t(translation)
    }

    getDelegates = (target) => {
        const targetOptions = this.surveyDetails.fetchData[target]
        const { industrySpecificInfo } = this.survey.metadata.productInfo
        return industrySpecificInfo[target] ? targetOptions.find(
            (a) => a.value === industrySpecificInfo[target],
        ) : undefined
    }

    getTemplateParams = () => {
        const {
            eventType,
            surveyType,
            respondentType,
        } = this.templateDetails.formData
        let eventTypeCode
        if (eventType) {
            const eventTypeId = eventType.value
            eventTypeCode = this.templateDetails.fetchData.eventTypes.find((i) => i.value === eventTypeId)?.type
        }
        let surveyTypeCode
        if (surveyType) surveyTypeCode = surveyType.value
        return {
            productType: eventTypeCode,
            surveyType: surveyTypeCode,
            respondentType,
            industry: R.pathOr(
                undefined,
                ['survey', 'metadata', 'productInfo', 'industrySpecificInfo', 'industry'],
                this.appStore,
            ),
        }
    }

    hideCEProductDetails = (fieldName) => {
        const { metadata: { productInfo: { industrySpecificInfo } } } = this.survey
        if (['locales', 'defaultLocale'].includes(fieldName)) return true
        if (['dateSection', 'startDate', 'endDate', 'eventSection', 'eventType'].includes(fieldName)) return false
        if (fieldName === 'respondentType') return !industrySpecificInfo.productType === 'CE3P'
        if (['country', 'state', 'city', 'otherCity', 'venue', 'otherVenue'].includes(fieldName)) return !['H', 'L'].includes(industrySpecificInfo.eventType)
        return true
    }

    hideEMProductDetails = (fieldName) => ![
        'dateSection',
        'startDate',
        'endDate',
        'eventSection',
        'country',
        'city',
        'otherCity',
        'state',
        'venue',
        'otherVenue',
        'locales',
        'defaultLocale',
    ].includes(fieldName)

    hideEVProductDetails = (fieldName) => {
        const {
            metadata: { taxonomy, productInfo: { industrySpecificInfo } },
        } = this.survey

        if (['dateSection', 'startDate', 'endDate'].includes(fieldName)) return !['PE', 'R', 'PC', 'W', 'P365', 'WV', 'VE', 'SP'].includes(taxonomy.surveyType)
        if (['numVisitors'].includes(fieldName)) return (!['VI'].includes(taxonomy.respondentType) || taxonomy.surveyType !== 'PE')
        if (['numExhibitors'].includes(fieldName)) return (!['EX'].includes(taxonomy.respondentType) || taxonomy.surveyType !== 'PE')
        if (['country', 'state', 'city', 'otherCity', 'venue', 'otherVenue'].includes(fieldName)) {
            return (['VI'].includes(industrySpecificInfo.productType)
                    || taxonomy.surveyType !== 'PE')
        }
        return true
    }

    allIndustryHideCheck = (fieldName) => {
        const {
            metadata: { taxonomy, productInfo: { industrySpecificInfo } },
        } = this.survey
        if (['eventType'].includes(fieldName)) {
            return !(['VI', 'EX'].includes(taxonomy.respondentType)
                    && industrySpecificInfo.productType !== 'HY')
        }
        if (['dateSection', 'startDate', 'endDate'].includes(fieldName)) {
            return (['', 'NN'].includes(taxonomy.respondentType)
                && ['', 'GE'].includes(taxonomy.surveyType))
        }
        if (['eventSection', 'locales', 'defaultLocale'].includes(fieldName)) {
            return false
        }
        if (fieldName === 'eventContains') {
            return !['VI'].includes(industrySpecificInfo.productType)
        }
        return true
    }

    hideProductDetailsFields = (industry, fieldName) => {
        if (industry === 'CE') {
            if (this.surveyType === 'EM') {
                return this.hideEMProductDetails(fieldName)
            }
            return this.hideCEProductDetails(fieldName)
        }

        if (industry === 'EV') {
            return this.hideEVProductDetails(fieldName)
        }

        return this.allIndustryHideCheck(fieldName)
    }

    hideSurveyDetailsForm = (industry, fieldName) => {
        const basicFields = ['surveyName', 'respondentType', 'surveyType', 'locales', 'defaultLocale']
        if (this.isRapidSurvey) return ['surveyType'].includes(fieldName)
        if (industry === 'CE') return true
        if (industry === 'EV' && basicFields.includes(fieldName)) return false
        if (industry === 'VENU' && basicFields.concat(['startDate', 'endDate']).includes(fieldName)) return false
        // for industires EXPO, DENT, VA, PU
        // hide respondentType for DENT
        if (industry === 'DENT' && fieldName === 'respondentType') return true
        if (['surveyName', 'respondentType', 'surveyType'].includes(fieldName)) return false

        return true
    }

    createSurveyDetailsForm = () => {
        const disableSurveyType = this.survey.metadata.taxonomy.respondentType === 'NN'
        const isVenueSurvey = this.industry === 'VENU'

        this.surveyDetails.formFields = [
            {
                type: 'input',
                name: 'surveyName',
                label: i18n.t('surveyWizard.surveyDetails.surveyName'),
                placeholder: i18n.t('views.survey.wizard.placeholderText.surveyName'),
                defaultValue: this.surveyDetails.formData.surveyName,
                isMandatory: true,
                errorMessage: `${i18n.t('surveyWizard.surveyDetails.surveyName')} ${i18n.t('required')}`,
                isHide: this.hideSurveyDetailsForm(this.industry, 'surveyName'),
            },
            {
                type: 'group',
                name: 'dateSection',
                className: 'rapidDates',
                elements: [
                    {
                        type: 'date',
                        name: 'startDate',
                        label: i18n.t(`settings_${isVenueSurvey ? 'survey' : 'event'}_open_date`),
                        placeholder: i18n.t('form.date.placeholder'),
                        value: this.surveyDetails.formData.startDate,
                        isMandatory: true,
                        maxDate: this.surveyDetails.formData.endDate,
                        errorMessage: `${i18n.t(
                            `views.survey.wizard.${isVenueSurvey ? 'VENU' : 'general'}.form.dates.startdate.label`,
                        )} ${i18n.t('required')}`,
                        isHide: this.hideProductDetailsFields(this.industry, 'startDate'),
                        allowManualDateEdit: true,
                    },
                    {
                        type: 'date',
                        name: 'endDate',
                        label: i18n.t(`settings_${isVenueSurvey ? 'survey' : 'event'}_close_date`),
                        placeholder: i18n.t('form.date.placeholder'),
                        value: this.surveyDetails.formData.endDate,
                        isMandatory: true,
                        minDate: this.surveyDetails.formData.startDate,
                        errorMessage: `${i18n.t(
                            `views.survey.wizard.${isVenueSurvey ? 'VENU' : 'general'}.form.dates.enddate.label`,
                        )} ${i18n.t('required')}`,
                        isHide: this.hideProductDetailsFields(this.industry, 'endDate'),
                        allowManualDateEdit: true,
                    },
                ],
                isHide: !this.isRapidSurvey && !isVenueSurvey,
            },
            {
                type: 'select',
                name: 'respondentType',
                label: i18n.t('views.survey.wizard.all.form.respondent_type.label'),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.surveyDetails.fetchData.respondentTypes.find(
                    (res) => (res.value === this.survey.metadata.taxonomy.respondentType),
                ),
                options: this.surveyDetails.fetchData.respondentTypes,
                isMandatory: true,
                errorMessage: `${i18n.t('views.survey.wizard.all.form.respondent_type.label')} ${i18n.t('required')}`,
                isHide: this.hideSurveyDetailsForm(this.industry, 'respondentType'),
            },
            {
                type: 'select',
                name: 'surveyType',
                label: i18n.t('views.survey.wizard.all.form.survey_type.label'),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.surveyDetails.fetchData.surveyTypes.find(
                    (survey) => (survey.value === this.survey.metadata.taxonomy.surveyType),
                ),
                options: this.surveyDetails.fetchData.surveyTypes,
                isMandatory: true,
                errorMessage: `${i18n.t('views.survey.wizard.all.form.survey_type.label')} ${i18n.t('required')}`,
                showDisabled: disableSurveyType,
                isHide: this.hideSurveyDetailsForm(this.industry, 'surveyType'),
            },
            {
                type: 'custom-tag-fields',
                name: 'customTagFields',
                values: this.surveyRecordTags,
                options: this.isRapidSurvey ? this.surveyAvailableTags : [], // doesn't render without options
                onBlur: this.setSurveyTag,
            },
        ]
    }

    createTemplateDetailsForm = () => {
        const { appStore } = this
        const isExploriTemplate = appStore.userData.clientId === 1
        const templateParams = this.getTemplateParams()
        const isVENU = templateParams.industry === 'VENU'
        this.templateDetails.formFields = [
            {
                type: 'input',
                name: 'surveyName',
                label: i18n.t('templates.templateName'),
                placeholder: i18n.t('views.survey.wizard.placeholderText.templateName'),
                defaultValue: this.templateDetails.formData.surveyName,
                isMandatory: true,
                errorMessage: `${i18n.t('templates.templateName')} ${i18n.t('required')}`,
            },
            {
                type: 'select',
                name: 'eventType',
                label: i18n.t(`templates.eventType${isVENU ? '.VENU' : ''}`),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.templateDetails.fetchData.eventTypes.find(
                    (ev) => ev.value === this.templateDetails.formData.eventType?.value,
                ),
                options: this.templateDetails.fetchData.eventTypes,
                isMandatory: true,
                errorMessage: `${i18n.t('templates.eventType')} ${i18n.t('required')}`,
            },
            {
                type: 'select',
                name: 'surveyType',
                label: i18n.t('templates.surveyType'),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.templateDetails.fetchData.surveyTypes.find(
                    (survey) => (survey.value === this.survey.metadata.taxonomy.surveyType),
                ),
                options: this.getSurveyTypeOptions(templateParams.industry, templateParams.productType),
                isMandatory: true,
                errorMessage: `${i18n.t('templates.surveyType')} ${i18n.t('required')}`,
                showDisabled: !this.templateDetails.formData.eventType,
            },
            {
                type: 'select',
                name: 'templateLicenceType',
                label: i18n.t('views.survey.wizard.all.form.template_licence_type.label'),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.templateDetails.fetchData.templateLicenceTypes.find(
                    (survey) => (survey.value === this.survey.metadata.taxonomy.templateLicenceType),
                ),
                options: this.templateDetails.fetchData.templateLicenceTypes,
                isMandatory: true,
                errorMessage: `${i18n.t('template.templateLicenceType')} ${i18n.t('required')}`,
                showDisabled: !this.templateDetails.formData.surveyType,
            },
            {
                type: 'select',
                name: 'respondentType',
                label: i18n.t('templates.respondentType'),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.templateDetails.fetchData.respondentTypes.find(
                    (res) => (res.value === this.survey.metadata.taxonomy.respondentType),
                ),
                options: this.templateDetails.fetchData.respondentTypes,
                isMandatory: true,
                errorMessage: `${i18n.t('templates.respondentType')} ${i18n.t('required')}`,
                showDisabled: !this.templateDetails.formData.surveyType,
            },
            {
                type: 'template-access-checkbox-accordion',
                name: 'templateAccess',
                exposeUpdateBody: this.setUpdateTemplateAccess,
                initBody: () => this.templateDetails.formData.templateAccessBody,
                isExploriTemplate,
            },
            {
                type: 'template-custom-tags-accordion',
                name: 'templateTags',
                exposeUpdateBody: this.setUpdateTemplateCustomTags,
                isReady: this.templateDetails.areCustomTagsParamsReady,
                templateParams,
            },
        ]
    }

    createProductDetailsForm = async () => {
        const hideCity = this.productDetails.fetchData.cities.length === 1
            || this.productDetails.formData.country?.value === undefined
            || (this.productDetails.formData.country.value === 'US' && this.productDetails.formData.state?.value === undefined)
        const hideOtherCity = this.productDetails.fetchData.cities.length === 1 ? false : this.productDetails.formData.city?.value !== -1
        const hideVenue = (this.productDetails.fetchData.venues.length <= 1) || this.productDetails.formData.city?.value === undefined
        const hideOtherVenue = this.productDetails.fetchData.venues.length === 1 ? false : this.productDetails.formData.venue?.value !== -1
        this.productDetails.formFields = [
            {
                type: 'custom',
                name: 'dateSection',
                comp: (
                    <div className="custom-section-heading">
                        {i18n.t('surveyWizard.productDetails.eventDetails')}
                    </div>
                ),
                isHide: this.hideProductDetailsFields(this.industry, 'dateSection'),
            },
            {
                type: 'date',
                name: 'startDate',
                label: i18n.t('surveyWizard.productDetails.startDate'),
                placeholder: i18n.t('form.date.placeholder'),
                value: this.productDetails.formData.startDate,
                isMandatory: true,
                maxDate: this.productDetails.formData.endDate,
                errorMessage: `${i18n.t('views.survey.wizard.general.form.dates.startdate.label')} ${i18n.t('required')}`,
                isHide: this.hideProductDetailsFields(this.industry, 'startDate'),
                allowManualDateEdit: true,
            },
            {
                type: 'date',
                name: 'endDate',
                label: i18n.t('surveyWizard.productDetails.endDate'),
                placeholder: i18n.t('form.date.placeholder'),
                value: this.productDetails.formData.endDate,
                isMandatory: true,
                minDate: this.productDetails.formData.startDate,
                errorMessage: `${i18n.t('views.survey.wizard.general.form.dates.enddate.label')} ${i18n.t('required')}`,
                isHide: this.hideProductDetailsFields(this.industry, 'endDate'),
                allowManualDateEdit: true,
            },
            {
                type: 'custom',
                name: 'eventSection',
                comp: (
                    <div className="custom-section-heading">
                        {i18n.t('surveyWizard.productDetails.surveyDetails')}
                    </div>
                ),
                isHide: this.hideProductDetailsFields(this.industry, 'eventSection'),
            },
            {
                type: 'select',
                name: 'respondentType',
                label: i18n.t('views.survey.wizard.all.form.respondent_type.label'),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.productDetails.fetchData.respondentTypes.find(
                    (res) => (res.value === this.survey.metadata.taxonomy.respondentType),
                ),
                options: this.productDetails.fetchData.respondentTypes.sort((a, b) => this.respondentTypesOrderCE.indexOf(a.label) - this.respondentTypesOrderCE.indexOf(b.label)),
                isMandatory: true,
                errorMessage: `${i18n.t('views.survey.wizard.all.form.respondent_type.label')} ${i18n.t('required')}`,
                isHide: this.hideProductDetailsFields(this.industry, 'respondentType'),
            },
            {
                type: 'select',
                name: 'eventType',
                label: i18n.t('views.survey.wizard.CE.stage.2.details.eventType.label'),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.productDetails.fetchData.eventType.find(
                    (res) => (res.value === this.survey.metadata.productInfo.industrySpecificInfo.eventType),
                ),
                options: this.productDetails.fetchData.eventType,
                isMandatory: true,
                errorMessage: `${i18n.t('views.survey.wizard.CE.stage.2.details.eventType.label')} ${i18n.t('required')}`,
                isHide: this.hideProductDetailsFields(this.industry, 'eventType'),
            },
            {
                type: 'select',
                name: 'numVisitors',
                label: this.getNumVisitorsLabel(this.survey.metadata.taxonomy.respondentType),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.productDetails.formData.numVisitors,
                options: this.productDetails.fetchData.numVisitors,
                isMandatory: true,
                errorMessage: `${this.getNumVisitorsLabel(this.survey.metadata.taxonomy.respondentType)} ${i18n.t('required')}`,
                isHide: this.hideProductDetailsFields(this.industry, 'numVisitors'),
            },
            {
                type: 'select',
                name: 'numExhibitors',
                label: this.getNumVisitorsLabel(this.survey.metadata.taxonomy.respondentType),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.productDetails.formData.numExhibitors,
                options: this.productDetails.fetchData.numExhibitors,
                isMandatory: true,
                errorMessage: `${this.getNumVisitorsLabel(this.survey.metadata.taxonomy.respondentType)} ${i18n.t('required')}`,
                isHide: this.hideProductDetailsFields(this.industry, 'numExhibitors'),
            },
            {
                type: 'select',
                name: 'country',
                label: i18n.t('views.survey.wizard.CE.stage.2.details.country.label'),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.productDetails.formData.country,
                options: this.productDetails.fetchData.countries,
                isMandatory: true,
                isSearchable: true,
                path: ['country', 'value'],
                errorMessage: `${i18n.t('views.survey.wizard.CE.stage.2.details.country.label')} ${i18n.t('required')}`,
                isHide: this.hideProductDetailsFields(this.industry, 'country'),
            },
            {
                type: 'select',
                name: 'state',
                label: i18n.t('views.survey.wizard.CE.stage.2.details.state.label'),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.productDetails.formData.state,
                options: this.productDetails.fetchData.states,
                isMandatory: true,
                isSearchable: true,
                path: ['state', 'value'],
                errorMessage: `${i18n.t('views.survey.wizard.CE.stage.2.details.state.label')} ${i18n.t('required')}`,
                isHide: this.hideProductDetailsFields(this.industry, 'state') || this.productDetails.fetchData.states.length === 0,
            },
            {
                type: 'select',
                name: 'city',
                label: i18n.t('views.survey.wizard.CE.stage.2.details.city.label'),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.productDetails.formData.city,
                options: this.productDetails.fetchData.cities,
                isMandatory: true,
                isSearchable: true,
                errorMessage: `${i18n.t('views.survey.wizard.CE.stage.2.details.city.label')} ${i18n.t('required')}`,
                path: ['city', 'value'],
                isHide: this.hideProductDetailsFields(this.industry, 'city') ? true : hideCity,
            },
            {
                type: 'input',
                name: 'otherCity',
                label: i18n.t('surveyWizard.productDetails.otherCity'),
                placeholder: i18n.t('form.input.placeholder'),
                defaultValue: this.productDetails.formData.otherCity,
                isMandatory: true,
                errorMessage: `${i18n.t('surveyWizard.productDetails.otherCity')} ${i18n.t('required')}`,
                isHide: this.hideProductDetailsFields(this.industry, 'otherCity') ? true : hideOtherCity,
            },
            {
                type: 'select',
                name: 'venue',
                label: i18n.t('views.survey.wizard.CE.stage.2.details.venue.label'),
                placeholder: i18n.t('form.select.placeholder'),
                value: this.productDetails.formData.venue,
                options: this.productDetails.fetchData.venues,
                isMandatory: true,
                isSearchable: true,
                path: ['venue', 'value'],
                errorMessage: `${i18n.t('views.survey.wizard.CE.stage.2.details.venue.label')} ${i18n.t('required')}`,
                isHide: this.hideProductDetailsFields(this.industry, 'venue') || hideVenue,
            },
            {
                type: 'input',
                name: 'otherVenue',
                label: i18n.t('surveyWizard.productDetails.otherVenue'),
                placeholder: i18n.t('form.input.placeholder'),
                defaultValue: this.productDetails.formData.otherVenue,
                onChange: this.onInputChange,
                isMandatory: true,
                errorMessage: `${i18n.t('surveyWizard.productDetails.otherVenue')} ${i18n.t('required')}`,
                isHide: this.hideProductDetailsFields(this.industry, 'otherVenue') ? true : hideOtherVenue,
            },
            ...this.createLocale(this.hideProductDetailsFields, this.productDetails),
            {
                type: 'checkbox-group',
                name: 'eventContains',
                label: i18n.t('views.survey.wizard.all.form.eventContains.label'),
                disabled: this.survey.closed,
                options: Object.keys(this.productDetails.fetchData.eventContains).map(
                    (key) => (
                        { label: this.productDetails.fetchData.eventContains[key], value: key }
                    ),
                ),
                value: this.survey.metadata.productInfo.industrySpecificInfo.eventContains,
                isHide: this.hideProductDetailsFields(this.industry, 'eventContains'),
            },
            {
                type: 'custom-tag-fields',
                name: 'customTagFields',
                values: this.surveyRecordTags,
                options: this.surveyAvailableTags, // doesn't render without options
                onBlur: this.setSurveyTag,
            },
        ]
    }

    assignProductSearch = ({ value, label }) => {
        const { survey: { metadata: { productInfo } } } = this
        const { CLIENT_TEMPLATE } = this.surveyOptionEnum
        this.surveyOptions[CLIENT_TEMPLATE].infoDescription = i18n.t(
            'create.survey.wizard.tooltipText.clientTemplate',
            { clientName: this.clientName },
        )

        if (value === -1 || value === undefined) {
            productInfo.productId = -1
            this.productName = label
            return
        }
        productInfo.productId = value.productId
        productInfo.industrySpecificInfo.productType = value.productType
        productInfo.industrySpecificInfo.portfolioId = value.portfolioId
        productInfo.industrySpecificInfo.divisionId = value.divisionId
        productInfo.industrySpecificInfo.productName = value.label
        this.selectedProduct = { value, label }
        this.productIsOnsite = value.isOnsite
    }

    getVenueDetails = () => {
        const {
            country, city, venue, state, otherCity, otherVenue,
        } = this.productDetails.formData
        const venueObj = {}

        if (R.hasPath(['value'], country)) venueObj.country = country
        if (R.hasPath(['value'], state)) venueObj.state = state
        if (R.hasPath(['value'], city)) venueObj.city = city
        if (R.hasPath(['value'], venue)) venueObj.venue = venue
        if (otherCity) venueObj.otherCity = otherCity
        if (otherVenue) venueObj.otherVenue = otherVenue
        return venueObj
    }

    assignProductDetails = () => {
        const {
            startDate, endDate, locales, defaultLocale,
            venue, numVisitors, numExhibitors, eventContains,
        } = this.productDetails.formData

        this.survey.metadata.productInfo.industrySpecificInfo.startDate = startDate
        this.survey.metadata.productInfo.industrySpecificInfo.endDate = endDate
        if (!['EV', 'CE'].includes(this.industry)) {
            this.survey.metadata.locales = locales.map((a) => a.value)
            this.survey.metadata.defaultLocale = defaultLocale.value
        }
        if (this.survey.metadata.productInfo.industrySpecificInfo.eventType !== 'V' || this.surveyType === 'EM' || !this.isRapidSurvey) {
            this.survey.metadata.productInfo.industrySpecificInfo.venueId = R.pathOr(undefined, ['value'], venue)
            this.survey.metadata.productInfo.industrySpecificInfo.venue = this.getVenueDetails()
        }

        if (numVisitors) this.survey.metadata.productInfo.industrySpecificInfo.numVisitors = numVisitors.value
        if (numExhibitors) this.survey.metadata.productInfo.industrySpecificInfo.numExhibitors = numExhibitors.value
        this.survey.metadata.productInfo.industrySpecificInfo.eventContains = eventContains || []
    }

    assignSurveyDetails = async () => {
        const { taxonomy, productInfo } = this.survey.metadata
        const { industrySpecificInfo } = productInfo
        const detailsName = this.isTemplate ? 'templateDetails' : 'surveyDetails'
        const {
            surveyName, startDate, endDate,
        } = this[detailsName].formData

        this.survey.name = surveyName

        if (this.industry === 'EV' && taxonomy.respondentType === 'PR') {
            industrySpecificInfo.venue = undefined
        }
        if (this.isRapidSurvey || this.industry === 'VENU') {
            this.survey.metadata.productInfo.industrySpecificInfo.startDate = startDate
            this.survey.metadata.productInfo.industrySpecificInfo.endDate = endDate
        }
        if (this.industry === 'EV' && taxonomy.surveyType !== 'PE') {
            industrySpecificInfo.venue = undefined
            industrySpecificInfo.startDate = undefined
            industrySpecificInfo.endDate = undefined
            industrySpecificInfo.numExhibitors = undefined
            industrySpecificInfo.numVisitors = undefined
        }

        if (this.isTemplate) {
            const { numVisitors, template } = defaultProdDetails
            const eventTypeId = this.templateDetails.formData.eventType.value
            const eventType = this.templateDetails.fetchData.eventTypes.find((i) => i.value === eventTypeId)?.type
            const portfolioId = this.templateDetails.fetchData.eventTypes.find(
                (i) => i.value === eventTypeId,
            )?.portfolioId
            this.survey.metadata.productInfo.industrySpecificInfo.startDate = template.startDate
            this.survey.metadata.productInfo.industrySpecificInfo.endDate = template.closeDate
            this.survey.metadata.closeDate = template.closeDate
            this.survey.metadata.productInfo.industrySpecificInfo.numVisitors = numVisitors
            this.survey.metadata.productInfo.industrySpecificInfo.portfolioId = portfolioId
            this.survey.metadata.productInfo.industrySpecificInfo.productType = eventType
        }
    }

    goBack = () => {
        this.curStage -= 1
        this.disableContinueBtn = false
    }

    goNext = () => {
        this.curStage += 1
    }

    validate = (fields, formData) => {
        const isValid = Object.keys(fields)
            .map((key) => {
                if (key === 'gender') return true
                const validate = R.pathOr(fields[key], ['validate'], fields[key])
                const path = R.pathOr(null, ['path'], fields[key])
                const value = !R.isNil(path)
                    ? R.pathOr(undefined, path)(formData)
                    : formData[key]
                const a = validate({
                    value,
                    name: key,
                }, true)
                return a
            })
            .every((valid) => valid)

        return isValid
    }

    continueFromProductSearch = () => {
        if (!this.handleValidate()) {
            return
        }
        this.goNext()
    }

    continueFromProductNew = async () => {
        if (!this.handleValidate()) {
            return
        }
        const newProduct = await this.saveNewProduct()
        if (newProduct) {
            this.selectedProduct = { value: newProduct, label: newProduct.label }
            this.hideProductNew = true
        }
    }

    backFromProductNew = () => {
        this.hideProductNew = true
        this.goBack()
    }

    continueFromSurveyDetails = async () => {
        if (!this.handleValidate()) {
            return
        }
        await this.assignSurveyDetails()
        this.goNext()
        this.disableContinueBtn = this.isRapidSurvey
    }

    setUpdateTemplateAccess = (fn) => {
        this.templateDetails.accessUpdateFn = fn
    }

    setUpdateTemplateCustomTags= (fn) => {
        this.templateDetails.customTagsUpdateFn = fn
    }

    validateTemplateAccessBody = () => {
        const body = this.templateDetails.formData.templateAccessBody
        if (Array.isArray(body) && body.length > 0) return true
        return false
    }

    saveTemplateAccess = async () => {
        const body = await this.templateDetails.accessUpdateFn?.() || []
        this.templateDetails.formData.templateAccessBody = body
    }

    continueFromTemplateDetails = async () => {
        await this.saveTemplateAccess()
        if (!this.handleValidate() || !this.validateTemplateAccessBody()) return
        await this.setTemplateTags()
        await this.assignSurveyDetails()
        this.goNext()
    }

    handleUpdateTemplateAccess = async (templateId) => {
        if (!this.validateTemplateAccessBody()) return
        try {
            const body = JSON.stringify(this.templateDetails.formData.templateAccessBody)
            await this.appStore.http.post(`/template/${templateId}/access`, body)
            this.appStore.addNotification(null, 'Template access rights updated', 'success')
        } catch (e) {
            console.error(e)
            this.appStore.addNotification(null, `Template access rights failed updating: ${e.message}`, 'error')
        }
    }

    handleUpdateTemplateCustomTags = async (templateId) => {
        if (this.templateDetails.formData.templateAccessBody.length < 1) return
        const { templateCustomTagBody } = this.templateDetails.formData
        await this.appStore.saveTemplateTags(templateId, templateCustomTagBody)
    }

    continueFromProductDetails = () => {
        if (!this.handleValidate()) {
            return
        }
        this.assignProductDetails()
        this.goNext()
    }

    saveNewProduct = async () => {
        const { productInfo } = this.survey.metadata
        const { isExploriAdmin, isSuperUser } = this.appStore

        // remove privilege fields if not an admin
        if (!(isExploriAdmin() || isSuperUser())) {
            delete this.product.privilegedFields
        }

        try {
            const newProduct = await this.appStore.http.post('/product', JSON.stringify(this.product))
            productInfo.productId = newProduct[0].productId
            productInfo.industrySpecificInfo.productType = this.product.productType
            productInfo.industrySpecificInfo.portfolioId = this.product.portfolioId
            productInfo.industrySpecificInfo.productName = this.product.name
            this.productIsOnsite = false
            this.saveProductTags(productInfo.productId)
            return this.product
        } catch (error) {
            console.log(`ProductEdit save error ${JSON.stringify(error)}`)
            const msg = R.pathOr('Error creating product', ['message'])(error)
            if (msg.match(/licence/gi)) {
                this.appStore.addNotification(
                    null, i18n.t('not_enough_licences_to_add_new_product'), 'error',
                )
            } else {
                this.appStore.addNotification(null, msg, 'error')
            }
            return false
        }
    }

    setBreakLinks = ({ value }) => {
        this.survey.breakLinks = value
    }

    setWizardStages = () => {
        const stages = {
            R: [
                {
                    stageNo: 0,
                    stageName: i18n.t('surveyWizard.stage.R.0.title'),
                    pageTitle: i18n.t('surveyWizard.stage.R.0.title'),
                    pageDesc: i18n.t('surveyWizard.stage.R.0.desc'),
                    component: () => (
                        <ProductSearch
                            wizardStore={this}
                            store={this.appStore}
                            handleContinue={this.continueFromProductSearch}
                        />
                    ),
                },
                {
                    stageNo: 0,
                    stageName: i18n.t('surveyWizard.stage.CE.1'),
                    pageTitle: i18n.t('surveyWizard.createNewRapidSurvey'),
                    pageDesc: i18n.t('surveyWizard.stage.R.0.b.desc'),
                    component: () => <ProductNew wizardStore={this} store={this.appStore} />,
                    back: this.backFromProductNew,
                    continue: this.continueFromProductNew,
                    isHide: this.hideProductNew,
                },
                {
                    stageNo: 1,
                    stageName: i18n.t('surveyWizard.stage.CE.1'),
                    pageTitle: i18n.t('surveyWizard.createNewRapidSurvey'),
                    pageDesc: i18n.t('surveyWizard.stage.R.0.b.desc'),
                    component: () => <SurveyDetails wizardStore={this} />,
                    back: this.goBack,
                    continue: this.continueFromSurveyDetails,
                },
                {
                    stageNo: 2,
                    stageName: i18n.t('views.survey.wizard.CE.stage.3.template.placeholder'),
                    pageTitle: i18n.t('views.survey.wizard.R.stage.2.info.title'),
                    pageDesc: i18n.t('views.survey.wizard.R.stage.2.info.desc'),
                    component: () => <SurveyCreate wizardStore={this} />,
                    back: this.goBack,
                    continue: this.createRapidSurvey,
                },
                {
                    stageNo: 3,
                    stageName: i18n.t('views.survey.wizard.R.stage.3.info.title'),
                    pageTitle: i18n.t('views.survey.wizard.R.stage.3.info.title'),
                    component: () => <Preview store={this.appStore} />,
                    // no back options because user could create multiple surveys
                    continue: this.goNext,
                },
                {
                    stageNo: 4,
                    stageName: i18n.t('views.survey.wizard.R.stage.4.title'),
                    pageTitle: i18n.t('views.survey.wizard.R.stage.4.info.title'),
                    pageDesc: i18n.t('views.survey.wizard.R.stage.4.info.text'),
                    component: () => <Distribute wizardStore={this} />,
                    back: this.goBack,
                    continue: () => {
                        location.href = `/survey/${this.surveyId}/setup#new`
                    },
                },
            ],
            CE: [
                {
                    stageNo: 0,
                    stageName: i18n.t('surveyWizard.stage.CE.0'),
                    pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                    pageDesc: i18n.t('surveyWizard.stage.CE.0.a.desc'),
                    component: () => (
                        <ProductSearch
                            wizardStore={this}
                            store={this.appStore}
                            handleContinue={this.continueFromProductSearch}
                        />
                    ),
                },
                {
                    stageNo: 0,
                    stageName: i18n.t('surveyWizard.stage.CE.0'),
                    pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                    pageDesc: i18n.t('surveyWizard.stage.CE.0.b.desc'),
                    component: () => <ProductNew wizardStore={this} store={this.appStore} />,
                    back: this.backFromProductNew,
                    continue: this.continueFromProductNew,
                    isHide: this.hideProductNew,
                },
                {
                    stageNo: 1,
                    stageName: i18n.t('surveyWizard.stage.CE.2'),
                    pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                    pageDesc: i18n.t('surveyWizard.stage.CE.2.desc'),
                    component: () => <ProductDetails wizardStore={this} store={this.appStore} key={uuidv4()} />,
                    back: this.goBack,
                    continue: this.continueFromProductDetails,
                },
                {
                    stageNo: 2,
                    stageName: i18n.t('surveyWizard.stage.CE.3'),
                    pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                    pageDesc: i18n.t('surveyWizard.stage.CE.3.desc'),
                    component: () => <SurveyCreate wizardStore={this} />,
                    back: this.goBack,
                    continue: this.createSurvey,
                },
            ],
            EM: [
                {
                    stageNo: 0,
                    stageName: i18n.t('surveyWizard.stage.EM.0'),
                    pageTitle: i18n.t('surveyWizard.stage.EM.0.title'),
                    pageDesc: i18n.t('surveyWizard.stage.EM.0.a.desc'),
                    component: () => (
                        <ProductSearch
                            wizardStore={this}
                            store={this.appStore}
                            handleContinue={this.continueFromProductSearch}
                        />
                    ),
                },
                {
                    stageNo: 0,
                    stageName: i18n.t('surveyWizard.stage.EM.0'),
                    pageTitle: i18n.t('surveyWizard.stage.EM.0.title'),
                    pageDesc: i18n.t('surveyWizard.stage.EM.0.b.desc'),
                    component: () => <ProductNew wizardStore={this} store={this.appStore} />,
                    back: this.backFromProductNew,
                    continue: this.continueFromProductNew,
                    isHide: this.hideProductNew,
                },
                {
                    stageNo: 1,
                    stageName: i18n.t('surveyWizard.stage.EM.1'),
                    pageTitle: i18n.t('surveyWizard.stage.EM.1.title'),
                    pageDesc: i18n.t('surveyWizard.stage.EM.1.desc'),
                    component: () => <ProductDetails wizardStore={this} store={this.appStore} key={uuidv4()} />,
                    back: this.goBack,
                    continue: this.continueFromProductDetails,
                },
                {
                    stageNo: 2,
                    stageName: i18n.t('surveyWizard.stage.EM.2'),
                    pageTitle: i18n.t('surveyWizard.stage.EM.2.title'),
                    pageDesc: i18n.t('surveyWizard.stage.EM.2.desc'),
                    component: () => <SurveyCreate wizardStore={this} />,
                    back: this.goBack,
                    continue: this.createSurvey,
                },
            ],
            T: [
                {
                    stageNo: 0,
                    stageName: i18n.t('surveyWizard.stage.T.0.stageName'),
                    pageTitle: i18n.t('surveyWizard.stage.T.0.pageTitle'),
                    pageDesc: i18n.t('surveyWizard.stage.T.0.desc'),
                    component: () => <TemplateDetails wizardStore={this} />,
                    back: this.goBack,
                    continue: this.continueFromTemplateDetails,
                },
                {
                    stageNo: 1,
                    stageName: i18n.t('views.survey.wizard.CE.stage.3.template.placeholder'),
                    pageTitle: i18n.t('views.survey.wizard.CE.stage.3.template.placeholder'),
                    pageDesc: i18n.t('views.survey.wizard.R.stage.3.info.desc'),
                    component: () => <SurveyCreate wizardStore={this} />,
                    back: this.goBack,
                    continue: this.createTemplate,
                },
            ],
            VENU: [
                {
                    stageNo: 0,
                    stageName: i18n.t('surveyWizard.stage.VENU.0'),
                    pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                    pageDesc: i18n.t('surveyWizard.stage.VENU.0.desc'),
                    component: () => (
                        <ProductSearch
                            wizardStore={this}
                            store={this.appStore}
                            handleContinue={this.continueFromProductSearch}
                        />
                    ),
                },
                {
                    stageNo: 0,
                    stageName: i18n.t('surveyWizard.stage.CE.0'),
                    pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                    pageDesc: i18n.t('surveyWizard.newEvent.VENU.desc'),
                    component: () => <ProductNew wizardStore={this} store={this.appStore} />,
                    back: this.backFromProductNew,
                    continue: this.continueFromProductNew,
                    isHide: this.hideProductNew,
                },
                {
                    stageNo: 1,
                    stageName: i18n.t('surveyWizard.stage.CE.1'),
                    pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                    pageDesc: i18n.t('surveyWizard.stage.CE.1.desc'),
                    component: () => <SurveyDetails wizardStore={this} />,
                    back: this.goBack,
                    continue: this.continueFromSurveyDetails,
                },
                {
                    stageNo: 2,
                    stageName: i18n.t('surveyWizard.stage.CE.3'),
                    pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                    pageDesc: i18n.t('surveyWizard.stage.CE.3.desc'),
                    component: () => <SurveyCreate wizardStore={this} />,
                    back: this.goBack,
                    continue: this.createSurvey,
                },
            ],
        }

        const commonStage = [
            {
                stageNo: 0,
                stageName: i18n.t('surveyWizard.stage.CE.0'),
                pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                pageDesc: i18n.t('surveyWizard.stage.CE.0.a.desc'),
                component: () => (
                    <ProductSearch
                        wizardStore={this}
                        store={this.appStore}
                        handleContinue={this.continueFromProductSearch}
                    />
                ),
            },
            {
                stageNo: 0,
                stageName: i18n.t('surveyWizard.stage.CE.0'),
                pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                pageDesc: i18n.t('surveyWizard.stage.CE.0.b.desc'),
                component: () => <ProductNew wizardStore={this} store={this.appStore} />,
                back: this.backFromProductNew,
                continue: this.continueFromProductNew,
                isHide: this.hideProductNew,
            },
            {
                stageNo: 1,
                stageName: i18n.t('surveyWizard.stage.CE.1', 'Survey details'),
                pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                pageDesc: i18n.t('surveyWizard.stage.CE.1.desc'),
                component: () => <SurveyDetails wizardStore={this} />,
                back: this.goBack,
                continue: this.continueFromSurveyDetails,
            },
            {
                stageNo: 2,
                stageName: i18n.t('surveyWizard.stage.CE.2', 'Event details'),
                pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                pageDesc: i18n.t('surveyWizard.stage.CE.2.desc'),
                component: () => <ProductDetails wizardStore={this} store={this.appStore} key={uuidv4()} />,
                back: this.goBack,
                continue: this.continueFromProductDetails,
                isHide: this.hideProductDetails,
            },
            {
                stageNo: 3,
                stageName: i18n.t('surveyWizard.stage.CE.3', 'Create survey'),
                pageTitle: i18n.t('surveyWizard.createNewSurvey'),
                pageDesc: i18n.t('surveyWizard.stage.CE.3.desc'),
                component: () => <SurveyCreate wizardStore={this} />,
                back: this.goBack,
                continue: this.createSurvey,
            },
        ]
        const industries = ['EV', 'EXPO', 'DENT', 'VA', 'PU']

        industries.forEach((industry) => {
            stages[industry] = commonStage
        })

        return stages[this.surveyType ?? this.industry]
    }
}

export default WizardStore
